import React, { useMemo, useState, useEffect } from "react";
import Modal from "../ui/Modal";
import { uploadMenuImage } from "../../services/menuApi";
import { useAuth } from "../../auth/AuthContext";
import OnlineImageSearch from "./OnlineImageSearch";

const MENU_IMAGE_REDIR = "https://mydata.tablemate.net/redir.php?url=";

/* ---------- tiny spinner ---------- */
const Spinner = () => (
  <div
    style={{
      width: 16,
      height: 16,
      border: "2px solid #ddd",
      borderTopColor: "red",
      borderRadius: "50%",
      animation: "spin .8s linear infinite",
    }}
  />
);
// inline keyframes (if you don’t already have one in CSS)
const spinStyle =
  document.getElementById("__spinstyle") ||
  (() => {
    const el = document.createElement("style");
    el.id = "__spinstyle";
    el.innerHTML = "@keyframes spin{to{transform:rotate(360deg)}}";
    document.head.appendChild(el);
    return el;
  })();

// heuristics for input types
const isBoolish = (v) =>
  v === 0 || v === 1 || v === "0" || v === "1" || v === true || v === false;
const toBool = (v) => v === 1 || v === "1" || v === true;
const isNumeric = (v) => v !== "" && v !== null && !isNaN(Number(v));
const labelize = (k) =>
  k.replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());

export default function GenericEditModal({
  open,
  onClose,
  entity,
  record,
  excludeKeys = ["description"],
  readOnlyKeys = ["id", "code", "subcode"],
  onSubmit,
  onAddItem,
  onDelete,
}) {
  const { tenantServerUrl } = useAuth();

  function categoryPreviewUrl(entity, rec, cacheBustToken) {
    const code =
      entity === "subcategory"
        ? rec?.subcode || rec?.code || ""
        : rec?.code || "";
    if (!code) return "";
    const host = String(tenantServerUrl || "")
      .replace(/\/+$/, "")
      .replace(/\/tablemate$/i, "");
    if (!host) return "";
    const upstream = `${host}/tablemate/admin/images/categories/${encodeURIComponent(
      code
    )}.jpg${cacheBustToken ? `?t=${cacheBustToken}` : ""}`;
    return `${MENU_IMAGE_REDIR}${encodeURIComponent(upstream)}`;
  }

  const initial = useMemo(() => ({ ...(record || {}) }), [record]);
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  // uploader state
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState("");
  const [cacheBust, setCacheBust] = useState(""); // bump to refresh preview
  const [expanded, setExpanded] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [localPreview, setLocalPreview] = useState("");

  useEffect(() => {
    setForm(initial);
    setLocalPreview("");
    setSearchOpen(false);
    setUploadErr("");
  }, [initial]);

  useEffect(() => {
    if (!expanded) return;
    const onKey = (e) => {
      if (e.key === "Escape") setExpanded(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [expanded]);

  // fields to render (all except excluded)
  const fields = useMemo(() => {
    const keys = Object.keys(initial || {});
    return keys.filter((k) => !excludeKeys.includes(k));
  }, [initial, excludeKeys]);

  const setVal = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const renderInput = (k) => {
    const v = record[k];
    const readOnly = readOnlyKeys.includes(k);
    const current = form[k] !== undefined ? form[k] : v; // <-- use form if user changed it

    const common = {
      style: {
        ...styles.input,
        ...(readOnly ? { background: "#f7f7f7", color: "#666" } : {}),
      },
      readOnly,
    };

    // JSON list → render as list (display-only)
    if (typeof v === "object") {
      try {
        const arr = JSON.parse(JSON.stringify(v));
        if (Array.isArray(arr)) {
          return (
            <div
              style={{
                border: "1px solid #ddd",
                borderRadius: 8,
                padding: "6px 8px",
                background: "#fff",
                maxHeight: 100,
                overflowY: "auto",
              }}
            >
              {arr.map((obj, i) => (
                <div key={i} style={{ marginBottom: 4, fontSize: 13 }}>
                  <span>
                    - {obj.name}
                    {obj.price != null ? " - P" + obj.price : " [" + obj.subcode + "]"}
                  </span>
                </div>
              ))}
              {arr.length === 0 && (
                <div style={{ color: "#777", fontSize: 12 }}>Empty list</div>
              )}
            </div>
          );
        }
      } catch (e) {
        // fall through
      }
    }

    // checkbox for boolean-ish
    if (
      !readOnly &&
      (isBoolish(v) ||
        /enabled|active|visibility|visible|available/i.test(k))
    ) {
      return (
        <input
          type="checkbox"
          checked={!!toBool(current)}
          onChange={(e) => setVal(k, e.target.checked ? 1 : 0)}
        />
      );
    }

    // number input
    if (isNumeric(v) || /price|order|sort|vat|tax|capacity/i.test(k)) {
      return (
        <input
          type="number"
          value={current ?? ""}
          onChange={(e) => !readOnly && setVal(k, e.target.value)}
          {...common}
        />
      );
    }

    // multiline for text
    if (/desc|description|ingredients|notes/i.test(k)) {
      return (
        <textarea
          rows={4}
          value={current ?? ""}
          onChange={(e) => !readOnly && setVal(k, e.target.value)}
          style={{ ...common.style, resize: "vertical" }}
          readOnly={readOnly}
        />
      );
    }

    // default text input
    return (
      <input
        value={current ?? ""}
        onChange={(e) => !readOnly && setVal(k, e.target.value)}
        {...common}
      />
    );
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    setErr("");
    // build patch of changed values only (skip read-only)
    const patch = {};
    for (const k of fields) {
      if (readOnlyKeys.includes(k)) continue;
      if (form[k] !== initial[k]) patch[k] = form[k];
    }
    if (!Object.keys(patch).length) {
      onClose?.();
      return;
    }

    try {
      setSaving(true);
      await onSubmit(patch);
      onClose?.();
    } catch (ex) {
      setErr(ex?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  // ---- Category/Subcategory image uploader ----
  const canShowCategoryUploader =
    entity === "category" || entity === "subcategory";
  const codeForUpload =
    (entity === "subcategory"
      ? form?.subcode || initial?.subcode || initial?.code
      : form?.code || initial?.code) || "";

  const previewUrl = categoryPreviewUrl(entity, initial, cacheBust);
  const effectivePreview = localPreview || previewUrl;

  const categoryLabel = entity === "subcategory" ? "subcategory" : "category";
  const searchName =
    String(form?.name || initial?.name || form?.category || "").trim();
  const searchDescription = String(
    form?.description || initial?.description || ""
  ).trim();

  const handlePick = async (ev) => {
    setUploadErr("");
    const file = ev?.target?.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setUploadErr("Please select an image file.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setUploadErr("Max file size is 5 MB.");
      return;
    }
    if (!codeForUpload) {
      setUploadErr("Missing code/subcode for naming.");
      return;
    }

    // Show instant local preview
    const objectUrl = URL.createObjectURL(file);
    setLocalPreview(objectUrl);

    try {
      setUploading(true);
      const result = await uploadMenuImage(tenantServerUrl,file, {
        code: codeForUpload,
        isCategory: true,
        tenantServerUrl: tenantServerUrl || undefined, // pass through for multi-tenant
      });
      console.log("Upload result:", result);

      if (result?.ok || result === true) {
        setUploadErr("Photo upload success!");
        // Force <img> to fetch the fresh server file (replaces local preview)
        setCacheBust(String(Date.now()));
        // Drop local preview (after <img> swaps to server URL)
        setLocalPreview("");
      } else {
        setUploadErr("Photo upload failed!");
      }
    } catch (ex) {
      setUploadErr(ex?.message || "Upload failed.");
    } finally {
      setUploading(false);
      // clear input so same file can be selected again if needed
      if (ev?.target) ev.target.value = "";
      // Revoke object URL to avoid leaks
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    }
  };

  return (
    <>
    <Modal
      open={open}
      onClose={() => !saving && !searchOpen && onClose?.()}
      title={`Edit ${entity[0].toUpperCase() + entity.slice(1)}`}
      footer={
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          {(entity === "category" || entity === "subcategory") &&
            typeof onDelete === "function" && (
              <button
                type="button"
                onClick={onDelete}
                style={{ ...styles.btn, borderColor: "#b3261e", color: "#b3261e" }}
              >
                Delete {entity === "category" ? "Category" : "Subcategory"}
              </button>
            )}
          {entity === "subcategory" && typeof onAddItem === "function" && (
            <button type="button" onClick={onAddItem} style={styles.btn}>
              Add Menu Item
            </button>
          )}
          <button disabled={saving} onClick={() => onClose?.()} style={styles.btn}>
            Cancel
          </button>
          <button disabled={saving} onClick={submit} style={styles.btnPrimary}>
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      }
    >
      <form onSubmit={submit}>
        {/* Field grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 3fr",
            gap: 12,
          }}
        >
          {fields.map((k) => (
            <label
              key={k}
              style={{ display: "flex", flexDirection: "column", gap: 4 }}
            >
              <span style={{ fontSize: 12, color: "#555" }}>
                {labelize(k)}{" "}
                {readOnlyKeys.includes(k) && (
                  <em
                    style={{
                      color: "#888",
                      fontStyle: "normal",
                    }}
                  >
                    (read-only)
                  </em>
                )}
              </span>
              {renderInput(k)}
            </label>
          ))}
        </div>

        {/* Image uploader for category/subcategory */}
        {canShowCategoryUploader && (
          <div
            style={{
              marginBottom: 16,
              padding: 12,
              border: "1px dashed #ddd",
              borderRadius: 10,
              background: "#fafafa",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 8,
              }}
            >
              <div style={{ fontWeight: 600 }}>Category Image</div>
              <div style={{ fontSize: 12, color: "#666" }}>
                Saved as{" "}
                <code>
                  /admin/images/categories/{codeForUpload || "<code>"}.jpg
                </code>
              </div>
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "200px 1fr",
                gap: 12,
                alignItems: "center",
              }}
            >
              <div
                style={{
                  width: 200,
                  height: 140,
                  border: "1px solid #eee",
                  borderRadius: 10,
                  overflow: "hidden",
                  display: "grid",
                  placeItems: "center",
                  background: "#fff",
                }}
              >
                {effectivePreview ? (
                  <img
                    src={effectivePreview}
                    alt=""
                    referrerPolicy="no-referrer"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                      cursor: "zoom-in",
                    }}
                    onClick={() => setExpanded(true)}
                  />
                ) : (
                  <span style={{ color: "#999", fontSize: 12 }}>No image</span>
                )}
              </div>
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <input
                  type="file"
                  accept="image/*"
                  onChange={handlePick}
                  disabled={!codeForUpload || uploading}
                />
                <button
                  type="button"
                  onClick={() => setSearchOpen(true)}
                  disabled={!codeForUpload || uploading || !tenantServerUrl}
                  style={styles.btn}
                  title={`Find a photo of this ${categoryLabel} online and save it`}
                >
                  Search online
                </button>
                <span style={{ fontSize: 12, color: "#666" }}>
                  {codeForUpload
                    ? `Using code: ${codeForUpload}`
                    : "Enter a code first"}
                </span>
                {uploading && <Spinner />}
                {!!uploadErr && (
                  <div style={{ color: "#b3261e", fontSize: 12 }}>
                    {uploadErr}
                  </div>
                )}
                {!!effectivePreview && (
                  <button
                    type="button"
                    onClick={() => setExpanded(true)}
                    style={styles.btn}
                  >
                    Expand
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
        {!!err && (
          <div
            style={{
              color: "#b3261e",
              fontSize: 12,
              marginTop: 8,
            }}
          >
            {err}
          </div>
        )}
      </form>

      {/* Fullscreen image expander */}
      {expanded && !!effectivePreview && (
        <div
          onClick={() => setExpanded(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.75)",
            display: "grid",
            placeItems: "center",
            zIndex: 9999,
            cursor: "zoom-out",
          }}
        >
          <img
            src={effectivePreview}
                    alt=""
                    referrerPolicy="no-referrer"
            style={{
              maxWidth: "95vw",
              maxHeight: "90vh",
              objectFit: "contain",
              boxShadow: "0 12px 40px rgba(0,0,0,0.5)",
              borderRadius: 12,
            }}
          />
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setExpanded(false);
            }}
            style={{
              position: "absolute",
              top: 20,
              right: 20,
              background: "#fff",
              border: "1px solid #ddd",
              borderRadius: 8,
              padding: "6px 10px",
              cursor: "pointer",
            }}
          >
            Close
          </button>
        </div>
      )}
    </Modal>
      {searchOpen && (
        <Modal
          open={searchOpen}
          onClose={() => setSearchOpen(false)}
          title={`Search for ${categoryLabel} image`}
          width={860}
          zIndex={10050}
        >
          <OnlineImageSearch
            itemName={searchName}
            itemDescription={searchDescription}
            existingFilename={`${codeForUpload}.jpg`}
            tenantServerUrl={tenantServerUrl}
            isCategory
            categoryCode={codeForUpload}
            placeholder={
              entity === "subcategory"
                ? "e.g. Coffees espresso latte"
                : "e.g. Beverages restaurant menu"
            }
            hint={`Ranked photos using the ${categoryLabel} name and description as keywords. Click one to download it as /admin/images/categories/${codeForUpload || "<code>"}.jpg.`}
            onApplied={({ thumb, remoteUrl }) => {
              setLocalPreview(thumb || remoteUrl || "");
              setCacheBust(String(Date.now()));
              setUploadErr("Photo saved.");
              setSearchOpen(false);
            }}
          />
        </Modal>
      )}
    </>
  );
}

const styles = {
  input: {
    padding: "8px 10px",
    border: "1px solid #ddd",
    borderRadius: 8,
    background: "#fff",
    fontSize: 14,
  },
  btn: {
    padding: "8px 12px",
    borderRadius: 8,
    border: "1px solid #ddd",
    background: "#fff",
    cursor: "pointer",
  },
  btnPrimary: {
    padding: "8px 12px",
    borderRadius: 8,
    border: "1px solid rgba(255, 0, 0, 1)",
    background: "rgba(255, 0, 0, 1)",
    color: "#fff",
    cursor: "pointer",
  },
};
